import React from 'react';
import { motion } from 'framer-motion';

const certifications = [
  {
    name: 'MERN Stack (Ethnus)',
    details: 'Highest grade: 94/100',
    link: 'https://drive.google.com/file/d/13s-qCgRKCYTTlPLK8vTDZ63RaPqws_DT/view?usp=sharing',
  },
  {
    name: 'Full Stack (GeeksforGeeks)',
    details: '',
    link: 'https://www.geeksforgeeks.org/certificate/88172b1e6754050545165f66e60ad398?utm_source=socials&utm_medium=cc_link',
  },
  {
    name: 'Machine Learning (Stanford University, deeplearning.ai)',
    details: '',
    link: 'https://www.coursera.org/account/accomplishments/specialization/certificate/0ADYIBJO3J4S',
  },
  {
    name: 'Django (Udemy, in progress)',
    details: '',
    link: '',
  },
];

const About = () => (
  <section className="py-12 bg-black text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
    <div className="max-w-2xl mx-auto px-4">
      <motion.h2
        className="text-3xl font-bold mb-4 text-blue-400"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        About Me
      </motion.h2>
      <motion.p
        className="text-lg text-gray-200 mb-4 text-1.5xl"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
      >
        I am a passionate and academically strong Computer Science student (CGPA: <span className="font-bold text-green-400">9.19</span>), interested in <span className="font-bold text-blue-400">Full Stack</span>, <span className="font-bold text-purple-400">Machine Learning</span>, and <span className="font-bold text-pink-400">GenAI</span> projects. I have hands-on experience building real-world solutions using these technologies.<br/><br/>
        <span className="font-semibold text-green-400 text-1.5xl">Achievements:</span> <br/>
        • Completed 250+ DSA problems on Leetcode <br/>
        • Completed 250+ problems on CodeChef <br/>
        • School highest mark in CS (Class 12) <br/>
        • School second mark (Class 10) <br/>
        • Consistently strong academic record
      </motion.p>
      <motion.div
        className="my-8 text-left"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <motion.h3
          className="text-2xl font-bold mb-2 text-purple-400 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
        >
          Experience
        </motion.h3>
        <motion.div
          className="bg-gray-900 rounded-lg p-4 mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="font-semibold text-lg text-blue-400">SDE Intern, Kognitive Networks</div>
          <div className="text-gray-300 text-sm mb-2">May 2025 – July 2025</div>
          <div className="text-gray-200 mb-2">
            <span className="font-bold">Project:</span> SearchBot (Hybrid Multimodal RAG Assistant)
          </div>
         <ul className="list-disc list-inside text-gray-300 mb-2 text-lg leading-relaxed">
  <li>🤖 Built <strong>SearchBot</strong>: a hybrid multimodal RAG assistant for internal knowledge retrieval.</li>
  <li>🔍 Engineered a smart pipeline combining <em>semantic vector search</em> + <em>BM25 keyword search</em> for high precision & recall.</li>
  <li>🖼️ Enabled <strong>multimodal retrieval</strong>: parsed PDFs, DOCX, TXT & used OCR to extract text from images/scans.</li>
  <li>🧠 Integrated <strong>LangChain</strong>, <strong>HuggingFace embeddings</strong>, and <strong>Groq’s LLaMA 3–70B</strong> for accurate, verifiable answers with references.</li>
</ul>

          <a href="https://drive.google.com/file/d/1mWwH1wj4h3gf_BewghZzbsOrSDlTdT2B/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300">View Internship Certificate (PDF)</a>
        </motion.div>
      </motion.div>
      <motion.div
        className="my-8 text-left"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <motion.h3
          className="font-bold mb-2 text-2.5xl text-green-400 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          viewport={{ once: true }}
        >
          Certifications
        </motion.h3>
        <motion.ul
          className="list-disc list-inside text-gray-300 text-sm mx-auto max-w-md"
          initial="hidden"
          whileInView="visible"
          variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }}
          viewport={{ once: true }}
        >
          {certifications.map((cert, idx) => (
            <motion.li
              key={cert.name}
              className="mb-2"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 * idx }}
              viewport={{ once: true }}
            >
              <span className="font-semibold text-white">{cert.name}</span>
              {cert.details && <span className="ml-2 text-green-400">({cert.details})</span>}
              {cert.link && (
                <a href={cert.link} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 underline hover:text-blue-300">View</a>
              )}
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </div>
  </section>
);

export default About; 